/**
 * SessionExpiredModal - PROJ-035: Session Expiration Handling
 *
 * Modal for in-place re-authentication when session expires.
 * Preserves user's current work context by allowing login without redirect.
 *
 * Follows project standard dialog styling (DraggableDialog pattern).
 */

import { useState, useEffect, useRef, type FormEvent } from 'react';
import {
  DraggableDialog,
  DraggableDialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/draggable-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Loader2, Lock, AlertCircle, LogIn, LogOut, X } from 'lucide-react';
import { login } from '@/lib/auth';
import { SESSION_EVENTS } from '@/hooks/useTokenManager';

interface SessionExpiredModalProps {
  open: boolean;
  onClose: () => void;
  userEmail?: string;
  // PROJ-035: Track expiration reason
  expirationType?: 'idle' | 'absolute' | 'unknown';
}

export function SessionExpiredModal({ open, onClose, userEmail, expirationType = 'unknown' }: SessionExpiredModalProps) {
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isReauthenticated, setIsReauthenticated] = useState(false);
  const passwordRef = useRef<HTMLInputElement>(null);

  // PROJ-035: Different messages based on expiration type
  const getMessage = () => {
    switch (expirationType) {
      case 'idle':
        return {
          title: 'Sesión Expirada por Inactividad',
          description: 'Tu sesión expiró debido a inactividad. Por favor, ingresa tu contraseña para continuar.',
        };
      case 'absolute':
        return {
          title: 'Sesión Expirada',
          description: 'Tu sesión alcanzó la duración máxima permitida. Por favor, ingresa tu contraseña para iniciar una nueva sesión.',
        };
      default:
        return {
          title: 'Sesión Expirada',
          description: 'Tu sesión ha expirado. Por favor, ingresa tu contraseña para continuar trabajando.',
        };
    }
  };

  const message = getMessage();

  // Focus password field when modal opens and reset state
  useEffect(() => {
    if (open) {
      setPassword('');
      setError(null);
      setIsReauthenticated(false);
      const timer = setTimeout(() => {
        passwordRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const handleReAuthenticate = async (e: FormEvent) => {
    e.preventDefault();

    if (!password.trim()) {
      setError('Ingresa tu contraseña para continuar');
      return;
    }

    if (!userEmail) {
      setError('No se pudo identificar el usuario. Por favor recarga la página.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await login({
        usernameOrEmail: userEmail,
        password: password,
      });

      // Mark as re-authenticated to prevent logout on close
      setIsReauthenticated(true);

      // Emit refresh event to notify other components
      window.dispatchEvent(new CustomEvent(SESSION_EVENTS.REFRESHED));

      // Optionally reload current data
      window.dispatchEvent(new CustomEvent('session:reauthenticated'));

      // Close modal (won't trigger logout because isReauthenticated is true)
      onClose();

    } catch (err) {
      console.error('[SessionExpiredModal] Re-authentication failed:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Error al re-autenticar. Verifica tu contraseña e intenta nuevamente.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    // Full logout - redirect to login page
    window.location.href = '/login?reason=session_expired';
  };

  // Handle dialog close - only logout if not successfully re-authenticated
  const handleDialogClose = (isOpen: boolean) => {
    if (!isOpen && !isReauthenticated) {
      handleLogout();
    }
  };

  return (
    <DraggableDialog open={open} onOpenChange={handleDialogClose}>
      <DraggableDialogContent
        className="sm:max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {/* Header - Standard project style */}
        <div className="px-6 py-4 border-b bg-background">
          <DialogHeader className="text-left">
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-amber-600" />
              {message.title}
            </DialogTitle>
            <DialogDescription>
              {message.description}
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Content */}
        <form id="session-reauth-form" onSubmit={handleReAuthenticate} className="px-6 py-4 space-y-4">
          {/* User identification */}
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-sm text-muted-foreground">Sesión activa como:</p>
            <p className="font-medium">{userEmail || 'Usuario desconocido'}</p>
          </div>

          {/* Error message */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Password field */}
          <div className="space-y-2">
            <Label htmlFor="session-password">Contraseña</Label>
            <Input
              ref={passwordRef}
              id="session-password"
              type="password"
              placeholder="Ingresa tu contraseña"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              autoComplete="current-password"
            />
          </div>

          {/* Help text */}
          <p className="text-xs text-muted-foreground">
            Tu trabajo no guardado se mantendrá mientras te re-autenticas.
          </p>
        </form>

        {/* Footer - Standard project style */}
        <TooltipProvider>
          <div className="flex items-center justify-between gap-3 border-t bg-background px-6 py-4">
            {/* LEFT: Secondary action */}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleLogout}
                disabled={isLoading}
                className="gap-2"
              >
                <LogOut className="h-4 w-4" />
                Ir al Login
              </Button>
            </div>

            {/* RIGHT: Primary action */}
            <div className="flex items-center gap-2">
              <Button
                type="submit"
                form="session-reauth-form"
                disabled={isLoading}
                className="gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Verificando...
                  </>
                ) : (
                  <>
                    <LogIn className="h-4 w-4" />
                    Continuar Sesión
                  </>
                )}
              </Button>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={handleLogout}
                    disabled={isLoading}
                    aria-label="Cerrar"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Cerrar e ir al login</TooltipContent>
              </Tooltip>
            </div>
          </div>
        </TooltipProvider>
      </DraggableDialogContent>
    </DraggableDialog>
  );
}

export default SessionExpiredModal;
